import { useState, useMemo } from 'react';
import { Box, Stack, TextField, Typography, Tooltip } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import VehicleCard, { statusColor } from './VehicleCard';
import { useLanguage } from '../../../utils/i18n';
import { useNotificationSettings } from '../../../stores/notificationSettingStore';

export default function VehicleData({ vehicleList, onSelectVehicle, followedVehicleId, onFollowVehicle, loadedStatusMap = {} }) {
  const { t } = useLanguage();
  const { settings: notifSettings } = useNotificationSettings();
  const [query, setQuery] = useState('');

  const mapped = useMemo(() => {
    const list = vehicleList || [];
    const vehicleMap = new Map();

    list.forEach((v) => {
      const id = v.vehicle ?? v.info?.id ?? v.id ?? `${v.vehicle_license_plate ?? 'unknown'}-${v.imei_no ?? ''}`;
      const fleetName = v.vehicle_license_plate || v.info?.name || 'Unknown';
      const uniqueKey = id && id !== 'unknown-' ? String(id) : String(fleetName);

      if (!vehicleMap.has(uniqueKey)) {
        vehicleMap.set(uniqueKey, v);
      } else {
        const existing = vehicleMap.get(uniqueKey);
        const existingTime = existing.last_update || existing.info?.modified_date;
        const newTime = v.last_update || v.info?.modified_date;
        const existingMs = existingTime ? new Date(existingTime).getTime() : 0;
        const newMs = newTime ? new Date(newTime).getTime() : 0;
        if (newMs >= existingMs) {
          vehicleMap.set(uniqueKey, v);
        }
      }
    });

    const uniqueList = Array.from(vehicleMap.values());

    return uniqueList.map((v) => {
      const id = v.vehicle ?? v.info?.id ?? v.id ?? `${v.vehicle_license_plate ?? 'unknown'}-${v.imei_no ?? ''}`;
      const fleet_name = v.vehicle_license_plate || v.info?.name || 'Unknown';
      const status = v.status || v.info?.status_text || 'unknown';
      const vehicle_type = v.vehicle_type_name || v.info?.vehicle_type_name || v.vehicle_type || '-';
      const driver_name = v.driver_name || v.join?.driver_name || v.info?.driver_name || '-';
      const imei = v.imei_no || v.integration_imei || v.imei || '-';
      const vehicle_odometer_km = v.current_odo ?? v.info?.current_odo ?? 0;
      const last_location_time = v.last_update || v.info?.modified_date || '-';
      const last_connected_time = v.last_update || '-';
      const stop_duration = v.stop_duration ?? v.speed_message ?? (v.speed != null ? `${v.speed} Km/H` : '—');
      const engine_status = v.engine ?? v.info?.engine_status ?? v.engine_status ?? '-';
      const distance_traveled_km = v.distance_traveled_km ?? v.info?.total_distance ?? 0;
      const trip_time = v.trip_time ?? '-';

      let fuel_liter = null;
      if (typeof v.fuel === 'number') fuel_liter = v.fuel;
      else if (v.canbus && v.canbus.canFuelPercentage) fuel_liter = v.canbus.canFuelPercentage;
      else if (v.info && v.info.canFuelPercentage) fuel_liter = v.info.canFuelPercentage;
      else fuel_liter = v.fuel ?? (v.info?.fuel_capacity ? `${v.info.fuel_capacity}` : '-');

      const last_location = {
        address: v.street || v.location || `${v.sub_district || ''} ${v.district || ''}`.trim() || '-',
        is_location_updated: (v.gps || '').toString().toLowerCase() === 'update'
      };
      const total_work_hours = v.total_work_hours ?? v.info?.canEngineWorktime ?? '-';

      const rawData = v.raw || v;
      const speedMessage = String(v.raw?.speed_message || v.speed_message || rawData.info?.speed_message || '');
      const msgLower = speedMessage.toLowerCase();
      const eventName = String(v.raw?.event || v.event || rawData.alarm_type || '').toLowerCase();
      const harshEvent = String(v.raw?.harsh_event || v.harsh_event || rawData.info?.harsh_event || '').toLowerCase();

      const isHarshBraking =
        harshEvent.includes('braking') ||
        harshEvent.includes('breaking') ||
        msgLower.includes('harsh braking') ||
        eventName.includes('harsh braking');
      const isHarshAccel =
        harshEvent.includes('acceleration') || msgLower.includes('harsh acceleration') || eventName.includes('harsh acceleration');
      const isHarshCorner =
        harshEvent.includes('cornering') || msgLower.includes('harsh cornering') || eventName.includes('harsh cornering');

      const isPanic = eventName.includes('panic') || eventName.includes('sos');

      const spatialMaxSpeed = Number(v.raw?.spatial_max_speed || v.spatial_max_speed || rawData.info?.spatial_max_speed || 0);
      const speed = Number(v.raw?.speed || v.speed || rawData.info?.speed || 0);
      const isLoaded = loadedStatusMap[id] || false;
      const isOverSpeedSpatial = spatialMaxSpeed > 0 && speed > spatialMaxSpeed && isLoaded;

      const typeRaw = String(v.raw?.type || v.type || rawData.info?.type || '').toLowerCase();
      const isOverSpeedMessage =
        msgLower.includes('over') || eventName.includes('overspeed') || typeRaw.includes('over speed') || typeRaw.includes('overspeed');
      const limitSpeed = Number(v.vehicle?.max_speed || rawData.info?.max_speed || 70);
      const isOverSpeedLimit = engine_status === 'ON' && speed > limitSpeed;
      const isOverSpeed = isOverSpeedMessage || isOverSpeedLimit;

      let activeViolation = null;

      const spatialMinSpeed = Number(v.raw?.spatial_min_speed || v.spatial_min_speed || rawData.info?.spatial_min_speed || 0);
      const isUnderSpeedSpatial = spatialMinSpeed > 0 && speed > 0 && speed < spatialMinSpeed && isLoaded;
      const isUnderSpeedMessage = msgLower.includes('under') || eventName.includes('underspeed');

      if (isPanic && notifSettings.panicButton) {
        activeViolation = { type: 'panic', message: '🚨 Darurat! Panic Button!' };
      } else if (isHarshBraking && notifSettings.harshBraking) {
        activeViolation = { type: 'harsh_braking', message: 'Harsh Braking!' };
      } else if (isHarshAccel && notifSettings.harshAcceleration) {
        activeViolation = { type: 'harsh_accel', message: 'Harsh Acceleration!' };
      } else if (isHarshCorner && notifSettings.harshCornering) {
        activeViolation = { type: 'harsh_corner', message: 'Harsh Cornering!' };
      } else if (isOverSpeedSpatial) {
        activeViolation = {
          type: 'overspeed_spatial',
          message: `Over Speed in zone! ${speed} km/h (Max: ${spatialMaxSpeed})`
        };
      } else if (isUnderSpeedSpatial) {
        activeViolation = {
          type: 'underspeed_spatial',
          message: `Under Speed in zone! ${speed} km/h (Min: ${spatialMinSpeed})`
        };
      } else if (isOverSpeed && notifSettings.overSpeed) {
        activeViolation = { type: 'overspeed', message: isOverSpeedLimit ? `Overspeed (> ${limitSpeed} km/h)` : `Over Speed!` };
      } else if (isUnderSpeedMessage) {
        activeViolation = { type: 'underspeed', message: `Under Speed!` };
      }

      return {
        id,
        fleet_name,
        status,
        vehicle_type,
        driver_name,
        imei,
        vehicle_odometer_km,
        last_location_time,
        last_connected_time,
        stop_duration,
        engine_status,
        distance_traveled_km,
        trip_time,
        fuel_liter,
        total_work_hours,
        last_location,
        activeViolation,
        isLoaded,
        raw: v
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleList, notifSettings]);

  const filtered = useMemo(() => {
    if (!query) return mapped;
    const q = query.toString().trim().toLowerCase();
    return mapped.filter((m) => {
      const candidates = [m.raw?.vehicle_license_plate].filter(Boolean).map((s) => s.toString().toLowerCase());
      return candidates.some((c) => c.includes(q));
    });
  }, [mapped, query]);

  const sortedAndFiltered = useMemo(() => {
    if (!filtered || filtered.length === 0) return [];

    const followedItems = [];
    const violatingItems = [];
    const otherItems = [];

    const sortedFiltered = [...filtered].sort((a, b) => {
      const valA = a.last_location_time === '-' ? '' : a.last_location_time;
      const valB = b.last_location_time === '-' ? '' : b.last_location_time;
      if (valA > valB) return -1;
      if (valA < valB) return 1;
      return 0;
    });

    sortedFiltered.forEach((v) => {
      if (v.id === followedVehicleId) {
        followedItems.push(v);
      } else if (v.activeViolation) {
        violatingItems.push(v);
      } else {
        otherItems.push(v);
      }
    });

    return [...followedItems, ...violatingItems, ...otherItems];
  }, [filtered, followedVehicleId]);

  const summaryCounts = useMemo(() => {
    const counts = { moving: 0, idle: 0, parked: 0, stopped: 0, delay: 0 };
    sortedAndFiltered.forEach((v) => {
      const color = statusColor(v, v.isLoaded || false);
      if (color === 'success') counts.moving++;
      else if (color === 'warning') counts.idle++;
      else if (color === 'primary') counts.parked++;
      else if (color === 'error') counts.delay++;
      else counts.stopped++;
    });
    return counts;
  }, [sortedAndFiltered]);

  return (
    <Box sx={{ width: 1, mt: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ pb: 1, flexShrink: 0 }}>
        <TextField
          label={t('mapsTracking.searchVehicle', 'Cari Kendaraan')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="medium"
          fullWidth
          variant="outlined"
          sx={{ bgcolor: '#fff', borderRadius: 1 }}
        />
      </Box>

      <Stack
        spacing={1}
        sx={{
          pb: 12,
          flexGrow: 1,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(72, 93, 154, 0.3) transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(72, 93, 154, 0.3)',
            borderRadius: '10px',
            transition: 'background-color 0.3s ease'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(72, 93, 154, 0.6)'
          }
        }}
      >
        {sortedAndFiltered.length > 0 ? (
          sortedAndFiltered.map((v) => {
            const isFollowed = followedVehicleId === v.id;
            return (
              <Box
                key={v.id}
                onClick={() => onSelectVehicle && onSelectVehicle(v)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { background: 'action.hover', borderRadius: 1 },
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <VehicleCard v={v} isFollowed={isFollowed} onFollowVehicle={onFollowVehicle} />
              </Box>
            );
          })
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '250px',
              mt: 2,
              p: 3,
              mx: 2,
              textAlign: 'center',
              bgcolor: 'rgba(72, 93, 154, 0.03)',
              borderRadius: 1,
              border: '1px dashed rgba(72, 93, 154, 0.25)',
              transition: 'all 0.3s ease'
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
                boxShadow: '0px 8px 24px rgba(72, 93, 154, 0.12)'
              }}
            >
              <SearchOffIcon sx={{ fontSize: 36, color: '#485D9A', opacity: 0.8 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ color: '#485D9A', fontWeight: 700, mb: 1 }}>
              {t('mapsTracking.vehicleNotFound', 'Kendaraan Tidak Ditemukan')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {t('mapsTracking.noVehicleMatchKeyword', 'Tidak ada kendaraan yang cocok dengan "')}
              <b>{query}</b>
              {t('mapsTracking.pleaseUseAnotherKeyword', '". Silakan gunakan kata kunci lain.')}
            </Typography>
          </Box>
        )}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          px: 1,
          py: 0.5,
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: '#f8fafc',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8
        }}
      >
        <Tooltip title={`Moving: ${summaryCounts.moving}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {summaryCounts.moving > 99 ? '99+' : summaryCounts.moving}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title={`Idle: ${summaryCounts.idle}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {summaryCounts.idle > 99 ? '99+' : summaryCounts.idle}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title={`Stopped: ${summaryCounts.stopped}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {summaryCounts.stopped > 99 ? '99+' : summaryCounts.stopped}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title={`Parked: ${summaryCounts.parked}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {summaryCounts.parked > 99 ? '99+' : summaryCounts.parked}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title={`Delay/Maintenance: ${summaryCounts.delay}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {summaryCounts.delay > 99 ? '99+' : summaryCounts.delay}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
